/**
 * @desc Main vuex state file.  Actions and getters loaded as modules, global mutations in this file
 * @desc due to being unable to get rootState from a mutation module.
 *
 * @author Mike Hamilton <mike@mikehamilton.ca>
 */

import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";

// import actions
import globalActions from "./global/actions";

// import department vuex state modules
import mapLayers from "./modules/mapLayers";

// import imagry vuex state modules
import imagryLayers from "./modules/imagryLayers";

// import basemap vuex state modules
import basemapLayers from "./modules/basemapLayers";

import { cloneDeep } from "lodash";

Vue.use(Vuex);

export default new Vuex.Store({
  // Persist entire vuex state into localstorage
  plugins: [
    createPersistedState({
      // iterate this if doing major surgery to the app menu
      key: "geowb-v3",
      reducer: state => {
        // Deep clone vuex state so we are working with the copy and not a linked version
        const newState = cloneDeep(state);

        const filterObject = (obj, key) => {
          // Iterate through the state tree
          for (let i in obj) {
            if (!obj.hasOwnProperty(i)) continue;
            if (i == key) {
              // Delete any key that matches
              delete obj[key];
            } else if (typeof obj[i] == "object") {
              filterObject(obj[i], key);
            }
          }
          return obj;
        };
        // Return the new state tree that has all "geojson" keys removed
        return filterObject(newState, "geojson");
      }
    })
  ],
  // Inject vuex state modules into instance
  modules: {
    globalActions,
    mapLayers,
    imagryLayers,
    basemapLayers
  },
  mutations: {
    // mutation to update store with fetched geojson
    mutationGetGeoJson: (state, { data, dept, layer }) => {
      // Checks if new data from api is different from data in the store, and if so, updates the store.
      if (
        JSON.stringify(state.mapLayers[dept].layers[layer].geojson) !==
        JSON.stringify(data)
      ) {
        state.mapLayers[dept].layers[layer].geojson = data;
      }
    },
    mutationGetGeoJsonLoading: (state, { dept, layer, loadingState }) => {
      state.mapLayers[dept].layers[layer].loading = loadingState;
      state.general.loading = loadingState;
    },
    // mutation that sets the current viewport after every map moveend event
    mutationSetViewport(state, payload) {
      state.viewport.center = payload.center;
      state.viewport.bearing = payload.bearing;
      state.viewport.pitch = payload.pitch;
      state.viewport.zoom = payload.zoom;
    },
    // mutation that toggles dialogs visibility
    mutationDialogUi(state, payload) {
      state.ui.dialogs[payload.dialog] = !state.ui.dialogs[payload.dialog];
    },
    // mutation that toggles map layers visibility
    mutationMapLayer(state, payload) {
      state.mapLayers[payload.dept].layers[
        payload.layer
      ].visibility.mapView = !state.mapLayers[payload.dept].layers[
        payload.layer
      ].visibility.mapView;
    },
    // mutation that specifically sets a map layer's visibility
    mutationMapLayerVisible(state, payload) {
      state.mapLayers[payload.dept].layers[payload.layer].visibility.mapView =
        payload.visible;
    },
    // mutation that toggles imagry layers visibility
    mutationImagryLayer(state, payload) {
      state.imagryLayers[payload.layer].visibility = !state.imagryLayers[
        payload.layer
      ].visibility;
    },
    // mutation that toggles basemap layers visibility
    mutationBasemapLayer(state, payload) {
      state.basemapLayers[payload.layer].visibility = !state.basemapLayers[
        payload.layer
      ].visibility;
    },
    // mutation that toggles the list layer visibility
    mutationListLayer(state, payload) {
      state.mapLayers[payload.dept].layers[
        payload.layer
      ].visibility.listView = !state.mapLayers[payload.dept].layers[
        payload.layer
      ].visibility.listView;
    },
    mutationCloseListLayer(state, payload) {
      state.mapLayers[payload.dept].layers[
        payload.layer
      ].visibility.listView = false;
    },
    // mutation that toggles the single layer visibility
    mutationSingleLayer(state, payload) {
      state.mapLayers[payload.dept].layers[
        payload.layer
      ].visibility.singleView = payload.state;
      state.mapLayers[payload.dept].layers[payload.layer].visibility.singleId =
        payload.id;
    },
    // mutation that sets the auth profile
    mutationAuthProfile(state, payload) {
      state.auth.profile = payload.profile;
    },
    // mutation that sets the auth token
    mutationAuthToken(state, payload) {
      state.auth.token = payload.token;
    },
    // mutation that sets the auth state
    mutationAuthState(state, payload) {
      state.auth.state = payload.bool;
    },
    mutationDialogSnackbarSaved(state, payload) {
      state.ui.dialogs.snackbar_saved = payload.state;
    },
    mutationUiImageFilename(state, payload) {
      state.ui.image.filename = payload;
    },
    mutationZoomTo(state, payload) {
      state.zoomTo.center = payload.center;
      state.zoomTo.level = payload.level;
      state.zoomTo.timestamp = Date.now();
    },
    // Mutation that takes a point and pushes into store with a timestap
    // so that we can draw a point on the map
    mutationDrawPoint(state, payload) {
      state.drawPoint.center = payload.center;
      state.drawPoint.timestamp = Date.now();
    }
  },
  // default state starting point
  state: {
    general: {
      title: "GeoWB",
      copyright: {
        app: "© 2018 by Mike Hamilton.  All rights reserved.",
        data:
          "Data by Heather Dufty and is © Whistler Blackcomb. All rights reserved."
      },
      interval: 120000,
      intervalLow: 600000,
      loading: false
    },
    viewport: {
      center: {},
      bearing: {},
      pitch: {},
      zoom: {}
    },
    zoomTo: {
      center: {},
      level: {},
      timestamp: {}
    },
    drawPoint: {
      center: {},
      timestamp: {}
    },
    ui: {
      dialogs: {
        legal: false,
        login: false,
        help: false,
        unauthorized: false,
        snackbar_saved: false,
        watermap: false
      },
      image: {
        filename: null
      }
    },
    auth: {
      state: false,
      profile: {},
      token: null
    }
  }
});
